import { useMutation, useQuery } from "@apollo/client";
import { Drawer } from "antd";
import { get, isFunction, map, maxBy } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import WithContactCard from "screens/Private/Broker/Clients/Card";
import { findUserName } from "screens/Private/Broker/Dashboard/View";
import GenerateLinkModal from "screens/Private/Broker/Projects/List/Subscriptions/GenerateLinkModal";
import Loading from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import { TERMINATE_TASK } from "utils/api/graphql/mutations/task";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { SUBSCRIPTIONS_LIST } from "utils/api/graphql/queries/subscription";
import { TASKS } from "utils/api/graphql/queries/tasks";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import { USER_ACTIVITY } from "utils/api/graphql/queries/user-activity";
import {
  INITIAL_VALUES,
  LOGS_STATUSES_KEYS,
  SCREENS,
  TRACER_TYPES,
} from "utils/constants";
import TaskDetails from "./TaskDetails";
import TerminateTaskDrawer from "./TerminateTaskDrawer";

export const STATES = {
  GENERATE_LINK: "GENERATE_LINK",
  NAVIGATE: "NAVIGATE",
};

const TaskDetailsDrawer = ({ open, onClose, task }) => {
  const take = 10;
  const { t } = useTranslation(NAME_SPACES.HOME);
  const navigate = useNavigate();
  const TRANSLATION = t("ACTIVITIES", { returnObjects: true });
  const [openTerminate, setOpenTerminate] = useState({
    open: false,
    state: null,
  });
  const [userActivities, setUserActivities] = useState([]);
  const [tracers, setTracers] = useState([]);
  const [openContactCard, setOpenContactCard] = useState(false);
  const [openGenerateLink, setOpenGenerateLink] = useState(false);
  const [result, setResult] = useState(task?.result);
  const [taskStatus, setTaskStatus] = useState(task?.status);
  const variables = {
    take,
    raw: [
      `"data" -> 'result' -> 'project' ->> 'id' = '${get(task, "project.id")}' 
        AND
        ("action" = 'updateSubscription' AND "data" -> 'input' -> 'data' ->> 'status' = 'SIGNED' 
        OR  
        "action" = 'updateSubscription' AND "data" -> 'input' -> 'data' ->> 'status' = 'PENDING'
        OR  
        "action" = 'addQuote'
        OR  
        "action" = 'addQualification'
         OR
        "action" = 'signDocuments'
        OR
        "action" = 'documentsSigned'
        OR
        "action" = 'terminateTask')
        `,
    ],
  };
  const { data: subscriptions, loading: subscriptionsLoading } = useQuery(
    SUBSCRIPTIONS_LIST,
    {
      skip: !get(task, "project.id"),
      variables: {
        where: { project: { id: get(task, "project.id") } },
        exclude: ["telecoms"],
      },
    },
  );

  const { data: project, loading: projectLoading } = useQuery(PROJECT, {
    skip: !get(task, "project.id"),
    variables: {
      where: { id: get(task, "project.id") },
      exclude: [],
    },
  });
  const [terminateTask] = useMutation(TERMINATE_TASK, {
    refetchQueries: [
      {
        query: TASKS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });
  useQuery(TRACERS, {
    fetchPolicy: "no-cache",
    variables: { where: { type: TRACER_TYPES.MANUAL } },
    onCompleted: ({ tracers: { data } }) => {
      setTracers(omitDeep(data, "__typename"));
    },
  });

  const matchStatusesWithProcess = (statuses) => {
    const result = map(
      statuses.filter(({ status }) => status),
      ({ status: { id: statusId, order } }) => {
        const allProcessStatuses = tracers.flatMap(
          (tracer) =>
            tracer?.flow?.process?.processStatuses
              ?.filter(({ id }) => id === statusId)
              .map((status) => ({
                ...status,
                tracer,
              })) || [],
        );
        const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
          return -Math.abs(candidate.order - order);
        });

        return {
          tracer: matchedStatus?.tracer || null,
          processStatus: matchedStatus || null,
        };
      },
    ).filter(({ tracer }) => tracer !== null);
    return maxBy(result, ({ processStatus: { order } }) => parseInt(order, 10));
  };

  const onNavigateProjectDetail = () => {
    const { tracer } = matchStatusesWithProcess(
      get(project, "project.statuses", []),
    );
    navigate(
      `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${get(
        project,
        "project.id",
      )}`,
      {
        state: {
          process: get(tracer, "flow.process"),
          tracer,
        },
      },
    );
  };
  const { loading: userActivitiesLoading } = useQuery(USER_ACTIVITY, {
    skip: !get(task, "project.id"),
    variables,
    onCompleted: (data) => {
      setUserActivities(
        get(data, "userActivities.data", []).map((log) => {
          const clientFirstName = findUserName(log, "firstname");
          const clientLastName = findUserName(log, "lastname");
          const actionLabel =
            get(log, LOGS_STATUSES_KEYS[log.action]) || log.action;
          const translatedActionLabel =
            TRANSLATION?.[actionLabel] || actionLabel;
          return {
            clientFirstName,
            clientLastName,
            createdDate: log.createdDate,
            label: translatedActionLabel,
          };
        }),
      );
    },
  });
  const onTerminate = ({ id, result }) => {
    if (!result.value) return;
    terminateTask({
      variables: {
        where: { id },
        data: {
          result,
        },
      },
      onCompleted: ({ terminateTask }) =>
        setTaskStatus(get(terminateTask, "status")),
    });
    const terminateAction = {
      [STATES.NAVIGATE]: onNavigateProjectDetail,
      [STATES.GENERATE_LINK]: () =>
        setOpenGenerateLink(get(openTerminate, "data", {})),
    };
    if (isFunction(terminateAction[openTerminate?.state]))
      terminateAction[openTerminate?.state]();
    setOpenTerminate(false);
  };

  const handleTerminate = (data) => {
    onTerminate(data);
    setOpenTerminate({ open: false });
  };

  const onTerminateDrawerClose = () => {
    setOpenTerminate({ open: false });
  };

  return (
    <>
      {open && (
        <Drawer
          className="task-details-drawer"
          width={1024}
          title={<></>}
          open={open}
          onClose={onClose}
          styles={{
            body: { padding: 0 },
          }}
        >
          {projectLoading ? (
            <Loading />
          ) : (
            <div className="task-details-drawer__content">
              <TaskDetails
                task={task}
                setOpenTerminate={setOpenTerminate}
                userActivities={userActivities}
                userActivitiesLoading={userActivitiesLoading}
                subscriptions={get(subscriptions, "subscriptionsList.data", [])}
                subscriptionsLoading={subscriptionsLoading}
                onNavigateProjectDetail={onNavigateProjectDetail}
                project={get(project, "project")}
                projectLoading={projectLoading}
                tracers={tracers}
                setOpenContactCard={setOpenContactCard}
                taskStatus={taskStatus}
              />
            </div>
          )}
        </Drawer>
      )}

      <TerminateTaskDrawer
        open={openTerminate?.open}
        onClose={onTerminateDrawerClose}
        task={task}
        onTerminate={handleTerminate}
        result={result}
        setResult={setResult}
      />

      {get(project, "project.contact.id") && (
        <WithContactCard
          contactId={get(project, "project.contact.id")}
          project={get(project, "project")}
          open={openContactCard}
          setOpen={setOpenContactCard}
          matchStatusesWithProcess={matchStatusesWithProcess}
          tracers={tracers}
          setTracers={setTracers}
        />
      )}

      <GenerateLinkModal
        open={openGenerateLink}
        setOpen={setOpenGenerateLink}
        template={get(project, "project.fields.processTemplate")}
      />
    </>
  );
};

export default TaskDetailsDrawer;
